import { PackageGuide } from '../../../interfaces';
import PackageGuides from './PackageGuides';
import {
  Box,
  ColumnLayout,
  Container,
  Header,
  Link,
  SpaceBetween,
} from '@amzn/awsui-components-react';
import ValueWithLabel from '../../ui/ValueWithLabel';
import { PACKAGE_STRINGS } from '../../../constants/UIStrings';
import { PackageData } from '@amzn/awsdev-docs-virtual-smiley-typescript-client';
import { ReactElement } from 'react';
import { getLocalizedPackageLocale } from '../packageLocale';

const getPackageGuides = (serializedGuides?: string): PackageGuide[] => {
  if (!serializedGuides) {
    return [];
  }

  try {
    const parsedGuides: unknown = JSON.parse(serializedGuides);
    return typeof parsedGuides === 'object' && parsedGuides !== null
      ? (Object.values(parsedGuides) as PackageGuide[])
      : [];
  } catch {
    return [];
  }
};

const PackageDetailData = ({
  packageData,
}: {
  packageData: PackageData;
}): ReactElement => {
  const guides = getPackageGuides(packageData.guides);
  const guideComponents = guides.map((guide: PackageGuide) => {
    return (
      packageData.pathslug && (
        <PackageGuides
          guide={guide}
          key={guide.pathslug}
          packagePathSlug={packageData.pathslug}
        />
      )
    );
  });
  const packageLocale = getLocalizedPackageLocale(packageData);
  const packageName = encodeURIComponent(packageData.packageName);
  const packageTitle = encodeURIComponent(packageData.title ?? '');

  return (
    <Box margin={{ top: 'l', bottom: 'xxl' }}>
      <SpaceBetween size='l'>
        <Header
          variant='h1'
          description={
            packageLocale
              ? `Localized package (${packageLocale}). Package information is view-only.`
              : undefined
          }
          info={
            <Link
              external
              variant='info'
              href={`https://code.amazon.com/packages/${packageData.packageName}`}
            >
              View on code.amazon
            </Link>
          }
        >
          {packageData.packageName}
        </Header>
        <Container
          header={
            <Header
              variant='h2'
              description={PACKAGE_STRINGS['Naming conventions']}
            >
              Naming conventions
            </Header>
          }
        >
          <ColumnLayout columns={4} variant='text-grid'>
            <ValueWithLabel label='Service ID'>
              {packageData.serviceId ?? '-'}
            </ValueWithLabel>
            <ValueWithLabel label='Title'>{packageData.title}</ValueWithLabel>
            <ValueWithLabel label='Service pathslug'>
              {packageData.pathslug}
            </ValueWithLabel>
            <ValueWithLabel label='Feedback CTI'>
              {packageData.feedbackCTI ?? '-'}
            </ValueWithLabel>
            <ValueWithLabel label='Feedback SIM'>
              {packageData.feedbackSIM ?? '-'}
            </ValueWithLabel>
          </ColumnLayout>
        </Container>
        <Container header={<Header variant='h2'>TCX Proof of Concept</Header>}>
          <ColumnLayout columns={2} variant='text-grid'>
            <ValueWithLabel label='Quicksight Analytics'>
              {
                <Link
                  external
                  variant='info'
                  href={`https://us-east-1.quicksight.aws.amazon.com/sn/dashboards/0ceab71d-4438-451b-8a1f-cf4a98e6c65b#p.Package=${packageName}&p.Service=${packageTitle}&p.Guide=All`}
                >
                  View package quicksight analytics
                </Link>
              }
            </ValueWithLabel>
          </ColumnLayout>
        </Container>
        <Header variant='h3'>Associated Guides</Header>
        {guideComponents}
        {/*<PackageOptionsTable*/}
        {/*  forumID={packageData.forumId}*/}
        {/*  consoleHelp={packageData.options?.consoleHelp}*/}
        {/*  regionSpecificImages={packageData.options?.regionSpecificImages}*/}
        {/*/>*/}
      </SpaceBetween>
    </Box>
  );
};

export default PackageDetailData;
